/**
 * Player settings: the values a player chooses, validated on the way in.
 *
 * Pure — no storage, no React. `src/storage/persistence.ts` handles reading and writing;
 * this module only decides what a valid settings object is.
 *
 * Validation is deliberately per-field and forgiving. Stored settings come off a device
 * where they may have been written by an older build, hand-edited, or half-corrupted, and
 * losing every preference because one field went bad is worse than keeping the good ones.
 */

export type Settings = {
  /** Multiplier on raw look input, applied to both mouse and touch. */
  lookSensitivity: number;
  /** When true, moving the pointer or thumb down looks up. */
  invertLookY: boolean;
};

export const MIN_LOOK_SENSITIVITY = 0.1;
export const MAX_LOOK_SENSITIVITY = 3;

export const DEFAULT_SETTINGS: Settings = {
  lookSensitivity: 1,
  invertLookY: false,
};

export function clampSensitivity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.lookSensitivity;
  return Math.min(MAX_LOOK_SENSITIVITY, Math.max(MIN_LOOK_SENSITIVITY, value));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Turn anything at all into a valid `Settings`, falling back field by field. */
export function parseSettings(value: unknown): Settings {
  if (!isPlainObject(value)) return { ...DEFAULT_SETTINGS };

  const { lookSensitivity, invertLookY } = value;

  return {
    lookSensitivity:
      typeof lookSensitivity === 'number'
        ? clampSensitivity(lookSensitivity)
        : DEFAULT_SETTINGS.lookSensitivity,
    invertLookY:
      typeof invertLookY === 'boolean' ? invertLookY : DEFAULT_SETTINGS.invertLookY,
  };
}
